import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom"; // For navigation
import { db } from "../firebase"; // Import Firestore instance
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  limit,
  where,
  Timestamp,
  doc,
  getDoc,
} from "firebase/firestore"; // Import Firestore document functions
import { useAuth } from "../contexts/AuthContext"; // Import useAuth
import { useView } from "../contexts/ViewContext"; // Import useView
import RecentVitals from "../components/dashboard/RecentVitals";
import RecentIncidents from "../components/dashboard/RecentIncidents";
import UpcomingAppointments from "../components/dashboard/UpcomingAppointments";
import RecentNotes from "../components/dashboard/RecentNotes";
import MedicationReminders from "../components/dashboard/MedicationReminders";
import TodaysAttention from "../components/dashboard/TodaysAttention";
import TodaysCareSnapshot from "../components/dashboard/TodaysCareSnapshot";
import JsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  EVENT_TYPES,
  fetchDailyTimelineEvents,
  fetchRangeTimelineEvents,
} from "../utils/dailyEvents";
import { getScheduledMedicationReminders } from "../utils/medicationSchedule";

const getLocalDateString = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const toDateSafe = (value) => {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  if (value instanceof Date) return value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatTimeLabel = (value) => {
  const date = toDateSafe(value);
  if (!date) return "";
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const getInventoryStockStatus = (item) => {
  const quantity = toNumber(item.quantityOnHand);
  const threshold = toNumber(item.lowStockThreshold);
  if (quantity <= 0) return "Out of stock";
  if (threshold > 0 && quantity <= threshold) return "Low stock";
  return "In stock";
};

const DAILY_CARE_LOG_PATH = "/daily-care-log";

const formatInventoryQuantity = (item) =>
  `${toNumber(item.quantityOnHand)} ${item.unitLabel || "unit"}`;

const getLatestEvent = (events, type) =>
  events.filter((event) => event.type === type).sort((a, b) => b.ts - a.ts)[0];

const hasDailyCareSource = (event) =>
  Boolean(event.data?.dailyCareLogId || event.data?.source === "dailyCareLog");

const hasRecordedVitals = (vitals = {}) =>
  Object.keys(vitals || {}).some((key) => key !== "note");

const countDailyCareSegmentItems = (dailyCareEvents = []) => {
  const counts = {
    vitalsCount: 0,
    medicationCount: 0,
    noteCount: 0,
    incidentCount: 0,
    taskCompleted: 0,
    latestVitalsEvent: null,
    latestMedicationEvent: null,
  };

  dailyCareEvents.forEach((event) => {
    const data = event.data || {};
    const segments = Array.isArray(data.segments) ? data.segments : [];
    const hasPayloadVitals = hasRecordedVitals(data.vitals);
    const meds = Array.isArray(data.medications) ? data.medications : [];
    const incidents = Array.isArray(data.incidents) ? data.incidents : [];
    const tasks = Array.isArray(data.tasks) ? data.tasks : [];

    const hasSegmentVitals = segments.some(
      (segment) =>
        segment.type === "vitals" && hasRecordedVitals(segment.data || {}),
    );
    const segmentMedicationCount = segments.reduce((total, segment) => {
      if (segment.type !== "meds") return total;
      return (
        total +
        (Array.isArray(segment.data?.medicationIds)
          ? segment.data.medicationIds.length
          : 0)
      );
    }, 0);
    const segmentNoteCount = segments.filter(
      (segment) =>
        segment.type === "note" &&
        String(segment.data?.text || "").trim() !== "",
    ).length;
    const segmentIncidentCount = segments.filter(
      (segment) =>
        segment.type === "incident" &&
        String(segment.data?.description || "").trim() !== "",
    ).length;
    const segmentTaskCount = segments.reduce((total, segment) => {
      if (segment.type !== "task" && segment.type !== "tasks") return total;
      const items = Array.isArray(segment.data?.items)
        ? segment.data.items
        : Array.isArray(segment.data?.checklist)
          ? segment.data.checklist
          : [];
      return total + items.filter((item) => item.completed !== false).length;
    }, 0);

    const hasVitals = hasPayloadVitals || hasSegmentVitals;
    const medicationCount =
      meds.length > 0 ? meds.length : segmentMedicationCount;
    const noteCount =
      String(data.noteText || "").trim() !== "" ? 1 : segmentNoteCount;
    const incidentCount =
      incidents.length > 0 ? incidents.length : segmentIncidentCount;
    const completedTasks =
      tasks.filter((task) => task.completed !== false).length +
      segmentTaskCount;

    if (hasVitals) {
      counts.vitalsCount += 1;
      if (!counts.latestVitalsEvent || event.ts > counts.latestVitalsEvent.ts) {
        counts.latestVitalsEvent = event;
      }
    }
    if (medicationCount > 0) {
      counts.medicationCount += medicationCount;
      if (
        !counts.latestMedicationEvent ||
        event.ts > counts.latestMedicationEvent.ts
      ) {
        counts.latestMedicationEvent = event;
      }
    }
    counts.noteCount += noteCount;
    counts.incidentCount += incidentCount;
    counts.taskCompleted += completedTasks;
  });

  return counts;
};

const buildTaskSummary = (taskLogs = [], completedTaskEvents = []) => {
  let taskTotal = 0;
  let taskCompleted = 0;
  let nextIncompleteTask = "";

  taskLogs.forEach((taskLog) => {
    const checklist = Array.isArray(taskLog.checklist) ? taskLog.checklist : [];
    checklist.forEach((item) => {
      taskTotal += 1;
      if (item.completed) {
        taskCompleted += 1;
      } else if (!nextIncompleteTask) {
        nextIncompleteTask = `${taskLog.title || "Task"}: ${item.text || "Checklist item"}`;
      }
    });
  });

  if (taskTotal === 0 && completedTaskEvents.length > 0) {
    taskTotal = completedTaskEvents.length;
    taskCompleted = completedTaskEvents.length;
  }

  return { taskTotal, taskCompleted, nextIncompleteTask };
};

const buildTodaySummary = (todayEvents = [], todayTaskLogs = []) => {
  const dailyCareEvents = todayEvents.filter(
    (event) => event.type === EVENT_TYPES.DAILY_CARE,
  );
  const dailyCareCounts = countDailyCareSegmentItems(dailyCareEvents);
  const standaloneVitalsEvents = todayEvents.filter(
    (event) => event.type === EVENT_TYPES.VITALS && !hasDailyCareSource(event),
  );
  const standaloneMedicationEvents = todayEvents.filter(
    (event) =>
      event.type === EVENT_TYPES.MEDICATION && !hasDailyCareSource(event),
  );
  const standaloneIncidentEvents = todayEvents.filter(
    (event) =>
      event.type === EVENT_TYPES.INCIDENT && !hasDailyCareSource(event),
  );
  const standaloneNoteEvents = todayEvents.filter(
    (event) => event.type === EVENT_TYPES.NOTE && !hasDailyCareSource(event),
  );
  const completedTaskEvents = todayEvents.filter(
    (event) => event.type === EVENT_TYPES.TASK && !hasDailyCareSource(event),
  );
  const taskSummary = buildTaskSummary(todayTaskLogs, completedTaskEvents);
  const latestVitals = [
    getLatestEvent(standaloneVitalsEvents, EVENT_TYPES.VITALS),
    dailyCareCounts.latestVitalsEvent,
  ]
    .filter(Boolean)
    .sort((a, b) => b.ts - a.ts)[0];
  const latestCare = getLatestEvent(todayEvents, EVENT_TYPES.DAILY_CARE);
  const latestMedication = [
    getLatestEvent(standaloneMedicationEvents, EVENT_TYPES.MEDICATION),
    dailyCareCounts.latestMedicationEvent,
  ]
    .filter(Boolean)
    .sort((a, b) => b.ts - a.ts)[0];

  return {
    vitalsCount: dailyCareCounts.vitalsCount + standaloneVitalsEvents.length,
    dailyCareCount: dailyCareEvents.length,
    medicationCount:
      dailyCareCounts.medicationCount + standaloneMedicationEvents.length,
    appointmentCount: todayEvents.filter(
      (event) => event.type === EVENT_TYPES.APPOINTMENT,
    ).length,
    incidentCount:
      dailyCareCounts.incidentCount + standaloneIncidentEvents.length,
    noteCount: dailyCareCounts.noteCount + standaloneNoteEvents.length,
    latestVitalsLabel: latestVitals
      ? `Last recorded ${formatTimeLabel(latestVitals.ts)}`
      : "",
    latestCareLabel: latestCare
      ? `Last logged ${formatTimeLabel(latestCare.ts)}`
      : "",
    latestMedicationLabel: latestMedication
      ? `Last administered ${formatTimeLabel(latestMedication.ts)}`
      : "",
    ...taskSummary,
    taskCompleted: taskSummary.taskCompleted + dailyCareCounts.taskCompleted,
  };
};

const GlanceSection = ({ title, eyebrow, children, className = "" }) => (
  <section
    className={`flex h-full min-h-[260px] flex-col rounded-3xl border border-slate-200/80 bg-white/95 p-5 shadow-sm md:p-6 ${className}`}
  >
    <div className="mb-4">
      {eyebrow && (
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
          {eyebrow}
        </p>
      )}
      <h2 className="mt-1 text-xl font-semibold tracking-tight text-slate-950">
        {title}
      </h2>
    </div>
    <div className="flex-1">{children}</div>
  </section>
);

// Utility to format non-vital events. The VITALS case is handled later with access to vitalsConfig mapping.
const baseEventDetails = (ev) => {
  const d = ev.data || {};
  switch (ev.type) {
    case EVENT_TYPES.MEDICATION: {
      const name = d.medicationName || d.medicationId || "Medication";
      const doseRaw = d.logDosage || d.dosageAdministered || d.dosage || "";
      const unit = d.logUnit || d.unit || "";
      const dose = doseRaw ? `${doseRaw}${unit}` : "";
      const notePart = d.notes ? ` | Notes: ${d.notes}` : "";
      const administeredByName =
        (d.createdBy && (d.createdBy.name || d.createdBy.uid)) ||
        (d.lastModifiedBy && (d.lastModifiedBy.name || d.lastModifiedBy.uid)) ||
        d.administeredBy ||
        "";
      let createdDate = null;
      if (d.createdAt && typeof d.createdAt.toDate === "function") {
        createdDate = d.createdAt.toDate();
      } else if (d.createdAt) {
        createdDate = new Date(d.createdAt);
      } else if (
        d.administeredAt &&
        typeof d.administeredAt.toDate === "function"
      ) {
        createdDate = d.administeredAt.toDate();
      } else {
        createdDate = null;
      }
      const createdStr = createdDate ? createdDate.toLocaleString() : "";
      const adminPart =
        administeredByName || createdStr
          ? ` | Administered by: ${administeredByName || "Unknown"}${
              createdStr ? ` ${createdStr}` : ""
            }`
          : "";
      return `Drank ${name} ${dose}${notePart}${adminPart}`.trim();
    }
    case EVENT_TYPES.APPOINTMENT: {
      const title = d.title || d.reason || "Appointment";
      const locPart = d.location ? ` | Location: ${d.location}` : "";
      const docPart = d.doctorName
        ? ` | Doctor/Specialist: ${d.doctorName}`
        : "";

      const toDateSafe = (val) => {
        if (!val) return null;
        if (typeof val.toDate === "function") return val.toDate();
        if (val instanceof Date) return val;
        try {
          const dt = new Date(val);
          return isNaN(dt.getTime()) ? null : dt;
        } catch (e) {
          return null;
        }
      };
      const formatTime = (dt) =>
        dt
          ? dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
          : "";
      const startDt = toDateSafe(d.startTime || d.dateTime);
      const endDt = toDateSafe(d.endTime);
      const startStr = formatTime(startDt);
      const endStr = formatTime(endDt);
      let timePart = "";
      if (startStr && endStr) {
        timePart = ` | From ${startStr} to ${endStr}`;
      } else if (startStr) {
        timePart = ` | At ${startStr}`;
      }

      return `Appointment: ${title}${locPart}${docPart}${timePart}`.trim();
    }
    case EVENT_TYPES.INCIDENT: {
      const label = d.type || d.incidentType || "";
      const desc = d.description || "Details";
      const action = d.actionTaken ? ` | Action: ${d.actionTaken}` : "";
      return `Incident - ${label}: ${desc}${action}`.trim();
    }
    case EVENT_TYPES.NOTE: {
      return `Notes - ${d.noteText || d.note || d.content || d.text || ""}`.trim();
    }
    case EVENT_TYPES.TASK: {
      const title = d.taskTitle || d.title || "Task";
      const item = d.itemText || d.text || "";
      return `Task - ${title}: ${item}`.trim();
    }
    default:
      return "";
  }
};

const DashboardPage = () => {
  const { currentUser, activeCareProfile } = useAuth(); // Get currentUser AND activeCareProfile
  const { manualMobileViewOverride } = useView(); // Get manual override from context

  // --- DEBUG LOG FOR activeCareProfile ---
  if (process.env.NODE_ENV === "development") {
    console.log("[DashboardPage] Active Care Profile Data:", activeCareProfile);
  }
  // --- END DEBUG LOG ---

  const navigate = useNavigate(); // Hook for navigation
  const [, setIsMobileView] = useState(
    manualMobileViewOverride !== null
      ? manualMobileViewOverride
      : window.innerWidth < 768,
  );

  // Tab state: "glance" or "summary"
  const [activeTab, setActiveTab] = useState("glance");

  // Daily Summary state
  // Load saved date for this browser tab, fallback to today
  const [summaryDate, setSummaryDate] = useState(
    () =>
      sessionStorage.getItem("dashboardSummaryDate") ||
      new Date().toISOString().split("T")[0],
  );

  // Date range for export (defaults to the selected daily date)
  const [summaryStartDate, setSummaryStartDate] = useState(() => summaryDate);
  const [summaryEndDate, setSummaryEndDate] = useState(() => summaryDate);
  const [isRangeExportOpen, setIsRangeExportOpen] = useState(false);

  // Helper to update date state + sessionStorage together
  const updateSummaryDate = (value) => {
    setSummaryDate(value);
    sessionStorage.setItem("dashboardSummaryDate", value);
  };
  const [dailyEvents, setDailyEvents] = useState([]);
  // Discard the loading state variable to avoid ESLint unused-var warning
  const [, setIsLoadingSummary] = useState(false);
  const todayDateStr = React.useMemo(() => getLocalDateString(), []);
  const [todayEvents, setTodayEvents] = useState([]);
  const [isLoadingTodayEvents, setIsLoadingTodayEvents] = useState(false);
  const [todayTaskLogs, setTodayTaskLogs] = useState([]);
  const [isLoadingTodayTasks, setIsLoadingTodayTasks] = useState(false);
  const [lowStockInventory, setLowStockInventory] = useState([]);
  const [isLoadingLowStockInventory, setIsLoadingLowStockInventory] =
    useState(false);

  const generateDailySummary = async (dateStr = summaryDate) => {
    if (!dateStr || !activeCareProfile) return;
    setIsLoadingSummary(true);

    try {
      const events = await fetchDailyTimelineEvents({
        db,
        careProfileId: activeCareProfile.id,
        dateStr,
      });
      setDailyEvents(events);
    } catch (err) {
      console.error("Error generating daily summary", err);
      window.alert("Failed to create summary. Check console.");
    } finally {
      setIsLoadingSummary(false);
    }
  };

  // auto fetch when date changes
  useEffect(() => {
    if (summaryDate) {
      generateDailySummary(summaryDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summaryDate, activeCareProfile]);

  useEffect(() => {
    if (!currentUser || !activeCareProfile) {
      setTodayEvents([]);
      setIsLoadingTodayEvents(false);
      return;
    }

    const fetchTodayEvents = async () => {
      setIsLoadingTodayEvents(true);
      try {
        const events = await fetchDailyTimelineEvents({
          db,
          careProfileId: activeCareProfile.id,
          dateStr: todayDateStr,
        });
        setTodayEvents(events);
      } catch (err) {
        console.error("Error fetching today's dashboard events", err);
        setTodayEvents([]);
      } finally {
        setIsLoadingTodayEvents(false);
      }
    };

    fetchTodayEvents();
  }, [currentUser, activeCareProfile, todayDateStr]);

  // Logo for PDF exports
  const [logoBase64, setLogoBase64] = useState(null);

  useEffect(() => {
    // Fetch MILA Care logo once for embedding in PDFs
    const fetchLogo = async () => {
      try {
        const response = await fetch("/logo.png");
        if (!response.ok) return;
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onloadend = () => setLogoBase64(reader.result);
        reader.readAsDataURL(blob);
      } catch (err) {
        console.error("Error fetching logo:", err);
      }
    };
    fetchLogo();
  }, []);

  // Removed unused exportDailyPdf to satisfy linter

  // Export activity over a date range to PDF
  const exportRangePdf = async () => {
    try {
      const startStr = summaryStartDate;
      const endStr = summaryEndDate;
      if (!startStr || !endStr) {
        window.alert("Please select both start and end dates.");
        return;
      }
      if (startStr > endStr) {
        window.alert("Start date must be on or before end date.");
        return;
      }
      if (!activeCareProfile) return;
      const events = await fetchRangeTimelineEvents({
        db,
        careProfileId: activeCareProfile.id,
        startDateStr: startStr,
        endDateStr: endStr,
      });

      if (events.length === 0) {
        window.alert("No activity found for the selected range.");
        return;
      }

      const doc = new JsPDF();
      const profileName = activeCareProfile?.profileName || "N/A";
      const head = [["Date", "Time", "Details"]];
      const body = events.map((ev) => [
        ev.ts.toLocaleDateString(),
        ev.ts.toLocaleTimeString(),
        formatEventDetails(ev),
      ]);

      autoTable(doc, {
        head,
        body,
        theme: "grid",
        margin: { top: 40 },
        didDrawPage: () => {
          if (logoBase64) {
            doc.addImage(logoBase64, "PNG", 14, 15, 20, 20);
          }
          doc.setFontSize(20);
          doc.text(`Activity for ${profileName}`, 40, 22);
          doc.setFontSize(12);
          doc.text(`Range: ${startStr} to ${endStr}`, 40, 30);
        },
      });

      doc.save(`activity_${startStr}_to_${endStr}.pdf`);
    } catch (err) {
      console.error("Error exporting range PDF", err);
      window.alert("Failed to export PDF. Check console for details.");
    }
  };

  // Vitals History State
  const [vitalsHistory, setVitalsHistory] = useState([]);
  const [isLoadingVitalsHistory, setIsLoadingVitalsHistory] = useState(true);

  // Vitals Edit State

  // Incident Reporting State (Dashboard specific)
  const [dashboardIncidents, setDashboardIncidents] = useState([]);

  // Appointments State (Dashboard specific)
  const [dashboardAppointments, setDashboardAppointments] = useState([]);
  const [isLoadingDashboardAppointments, setIsLoadingDashboardAppointments] =
    useState(true);

  // Notes & Observations State (Dashboard specific)
  const [dashboardNotes, setDashboardNotes] = useState([]);

  // MEDICATION STATE (Only for Administration Log)
  const [medsMedicationOptions, setMedsMedicationOptions] = useState([]);
  const [medsAdministrationHistory, setMedsAdministrationHistory] = useState(
    [],
  );
  const [isLoadingMeds, setIsLoadingMeds] = useState(true);
  // END MEDICATION STATE

  // Vitals configuration mapping (id -> name)
  const [vitalsConfig, setVitalsConfig] = useState([]);

  useEffect(() => {
    const fetchConfig = async () => {
      if (!activeCareProfile) return;
      try {
        const profileDoc = await getDoc(
          doc(db, "careProfiles", activeCareProfile.id),
        );
        if (profileDoc.exists() && profileDoc.data().vitalsConfig) {
          setVitalsConfig(profileDoc.data().vitalsConfig);
        }
      } catch (err) {
        console.error("Error fetching vitalsConfig for summary:", err);
      }
    };
    fetchConfig();
  }, [activeCareProfile]);

  useEffect(() => {
    if (!currentUser || !activeCareProfile) {
      setTodayTaskLogs([]);
      setIsLoadingTodayTasks(false);
      return;
    }

    setIsLoadingTodayTasks(true);
    const taskLogsRef = collection(
      db,
      `careProfiles/${activeCareProfile.id}/taskLogs`,
    );
    const q = query(taskLogsRef, where("date", "==", todayDateStr));
    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const logs = querySnapshot.docs.map((document) => ({
          id: document.id,
          ...document.data(),
        }));
        setTodayTaskLogs(logs);
        setIsLoadingTodayTasks(false);
      },
      (error) => {
        console.error(
          "Error fetching today's task logs for dashboard: ",
          error,
        );
        setTodayTaskLogs([]);
        setIsLoadingTodayTasks(false);
      },
    );

    return () => unsubscribe();
  }, [currentUser, activeCareProfile, todayDateStr]);

  useEffect(() => {
    if (!currentUser || !activeCareProfile) {
      setLowStockInventory([]);
      setIsLoadingLowStockInventory(false);
      return;
    }

    setIsLoadingLowStockInventory(true);
    const inventoryQuery = query(
      collection(db, "medicationInventory"),
      where("careProfileId", "==", activeCareProfile.id),
    );
    const unsubscribe = onSnapshot(
      inventoryQuery,
      (querySnapshot) => {
        const lowStockItems = querySnapshot.docs
          .map((document) => ({
            id: document.id,
            ...document.data(),
          }))
          .filter((item) =>
            ["Low stock", "Out of stock"].includes(
              getInventoryStockStatus(item),
            ),
          )
          .sort((a, b) => {
            const aStatus = getInventoryStockStatus(a);
            const bStatus = getInventoryStockStatus(b);
            if (aStatus !== bStatus) {
              return aStatus === "Out of stock" ? -1 : 1;
            }
            return toNumber(a.quantityOnHand) - toNumber(b.quantityOnHand);
          });
        setLowStockInventory(lowStockItems);
        setIsLoadingLowStockInventory(false);
      },
      (error) => {
        console.error(
          "Error fetching low stock inventory for dashboard:",
          error,
        );
        setLowStockInventory([]);
        setIsLoadingLowStockInventory(false);
      },
    );

    return () => unsubscribe();
  }, [currentUser, activeCareProfile]);

  const vitalIdToName = React.useMemo(() => {
    const map = {};
    vitalsConfig.forEach((v) => {
      map[v.id] = v.name;
    });
    return map;
  }, [vitalsConfig]);

  const formatEventDetails = (ev) => {
    if (ev.type === EVENT_TYPES.VITALS) {
      const vitals = ev.data?.vitals || {};
      const parts = Object.entries(vitals)
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([key, val]) => {
          const label = vitalIdToName[key] || key;
          if (val && typeof val === "object") {
            return `${label}: ${val.systolic ?? "-"}/${val.diastolic ?? "-"}`;
          }
          return `${label}: ${val}`;
        });
      let text = parts.join(", ");
      if (ev.data?.notes) {
        text += ` | Notes: ${ev.data.notes}`;
      }
      // Append recorder details: prefer last update; fallback to creation
      const d = ev.data || {};
      let createdAtDate = null;
      let lastModifiedAtDate = null;
      if (d.createdAt && typeof d.createdAt.toDate === "function") {
        createdAtDate = d.createdAt.toDate();
      } else if (d.createdAt) {
        createdAtDate = new Date(d.createdAt);
      }
      if (d.lastModifiedAt && typeof d.lastModifiedAt.toDate === "function") {
        lastModifiedAtDate = d.lastModifiedAt.toDate();
      } else if (d.lastModifiedAt) {
        lastModifiedAtDate = new Date(d.lastModifiedAt);
      }

      let recordedByName = "";
      let recordedAtDate = null;
      const lastModifiedDifferentFromCreated =
        lastModifiedAtDate &&
        (!createdAtDate ||
          lastModifiedAtDate.getTime() !== createdAtDate.getTime() ||
          (d.lastModifiedBy &&
            d.createdBy &&
            d.lastModifiedBy.uid !== d.createdBy.uid));

      if (lastModifiedDifferentFromCreated) {
        recordedByName =
          (d.lastModifiedBy &&
            (d.lastModifiedBy.name || d.lastModifiedBy.uid)) ||
          recordedByName;
        recordedAtDate = lastModifiedAtDate;
      } else if (d.createdBy || createdAtDate) {
        recordedByName =
          (d.createdBy && (d.createdBy.name || d.createdBy.uid)) ||
          recordedByName;
        recordedAtDate = createdAtDate;
      }

      const recordedAtStr = recordedAtDate
        ? recordedAtDate.toLocaleString()
        : "";
      if (recordedByName || recordedAtStr) {
        text += ` | Recorded by: ${recordedByName || "Unknown"}${
          recordedAtStr ? ` ${recordedAtStr}` : ""
        }`;
      }
      return text;
    }
    if (ev.type === EVENT_TYPES.DAILY_CARE) {
      const d = ev.data || {};
      const lines = ["Daily Care Log"];
      const formatMedicationSummary = (medications = []) =>
        medications
          .map((m) => {
            const status = m?.status || "Given";
            const name = m?.medicationName || "Medication";
            if (status !== "Given") {
              return `${name} ${status}${m?.holdReason ? ` (${m.holdReason})` : ""}`;
            }
            const dose = m?.dosage ? ` ${m.dosage}` : "";
            return `${name}${dose}`;
          })
          .join(", ");
      const getMedicationAdministeredBy = () => {
        const recordedBy =
          d.recordedBy || d.createdBy || d.lastModifiedBy || {};
        return recordedBy.name || recordedBy.email || recordedBy.uid || "";
      };

      const segments = Array.isArray(d.segments) ? d.segments : [];
      if (segments.length > 0) {
        segments.forEach((segment) => {
          if (segment.type === "vitals") {
            const vitalsData = segment.data || {};
            const vitalsParts = Object.entries(vitalsData)
              .filter(([key]) => key !== "note")
              .map(([key, val]) => {
                const label = vitalIdToName[key] || key;
                if (val && typeof val === "object") {
                  return `${label}: ${val.systolic ?? "-"}/${val.diastolic ?? "-"}`;
                }
                return `${label}: ${val}`;
              });
            if (vitalsParts.length > 0) {
              lines.push(`Vitals: ${vitalsParts.join(", ")}`);
            }
            if (vitalsData.note) {
              lines.push(`Vitals Note: ${vitalsData.note}`);
            }
          }

          if (segment.type === "meds") {
            const medText = formatMedicationSummary(
              Array.isArray(d.medications) ? d.medications : [],
            );
            if (medText) {
              lines.push(`Meds: ${medText}`);
            }
            const administeredBy = getMedicationAdministeredBy();
            if (administeredBy) {
              lines.push(`Administered by: ${administeredBy}`);
            }
            if (segment.data?.note) {
              lines.push(`Meds Note: ${segment.data.note}`);
            }
          }

          if (segment.type === "note" && segment.data?.text) {
            lines.push(`Notes: ${segment.data.text}`);
          }

          if (segment.type === "incident") {
            const incidentType = segment.data?.type || "Other";
            const description = segment.data?.description || "";
            const actionTaken = segment.data?.actionTaken || "";
            if (description || actionTaken) {
              lines.push(
                `Incident (${incidentType}): ${description}${actionTaken ? ` | Action: ${actionTaken}` : ""}`,
              );
            }
          }
        });
        return lines.join(" | ");
      }

      const vitals = d.vitals || {};
      const vitalsParts = Object.entries(vitals).map(([key, val]) => {
        const label = vitalIdToName[key] || key;
        if (val && typeof val === "object") {
          return `${label}: ${val.systolic ?? "-"}/${val.diastolic ?? "-"}`;
        }
        return `${label}: ${val}`;
      });
      if (vitalsParts.length > 0) {
        lines.push(`Vitals: ${vitalsParts.join(", ")}`);
      }
      if (d.vitalsNoteText) {
        lines.push(`Vitals Note: ${d.vitalsNoteText}`);
      }

      const meds = Array.isArray(d.medications) ? d.medications : [];
      if (meds.length > 0) {
        const medText = formatMedicationSummary(meds);
        lines.push(`Meds: ${medText}`);
        const administeredBy = getMedicationAdministeredBy();
        if (administeredBy) {
          lines.push(`Administered by: ${administeredBy}`);
        }
        if (meds[0]?.note) {
          lines.push(`Meds Note: ${meds[0].note}`);
        }
      }

      const incidents = Array.isArray(d.incidents) ? d.incidents : [];
      if (incidents.length > 0) {
        incidents.forEach((incident) => {
          const incidentType = incident?.type || "Other";
          const description = incident?.description || "";
          const actionTaken = incident?.actionTaken || "";
          lines.push(
            `Incident (${incidentType}): ${description}${actionTaken ? ` | Action: ${actionTaken}` : ""}`,
          );
        });
      }

      const noteText = d.noteText || d.narrativeText || "";
      if (noteText) {
        lines.push(`Notes: ${noteText}`);
      }

      return lines.join(" | ");
    }
    if (ev.type === EVENT_TYPES.TASK) {
      const title = ev.data?.taskTitle || "Task";
      const item = ev.data?.itemText || "";
      const who = ev.data?.doneByEmail || ev.data?.doneByName || "";
      const whenStr = ev.data?.doneAt
        ? (ev.data.doneAt instanceof Date
            ? ev.data.doneAt
            : typeof ev.data.doneAt?.toDate === "function"
              ? ev.data.doneAt.toDate()
              : new Date(ev.data.doneAt)
          ).toLocaleString()
        : "";
      let text = `Task - ${title}: ${item}`.trim();
      if (who || whenStr) {
        text += ` | Done by: ${who || "Unknown"}${whenStr ? ` ${whenStr}` : ""}`;
      }
      return text;
    }
    return baseEventDetails(ev);
  };

  useEffect(() => {
    // This effect is primarily for setting isMobileView based on window size and override
    // The original profile fetching logic is removed as activeCareProfile is now used directly.
    const handleResize = () => {
      setIsMobileView(
        manualMobileViewOverride !== null
          ? manualMobileViewOverride
          : window.innerWidth < 768,
      );
    };
    window.addEventListener("resize", handleResize);
    // Initial check
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, [manualMobileViewOverride]);

  // Effect to check for activeCareProfile and navigate if not present
  useEffect(() => {
    if (currentUser && !activeCareProfile) {
      console.log(
        "DashboardPage: No active care profile. Consider redirecting or showing a message.",
      );
      // Example: navigate('/profiles'); // Or show a prominent message on the dashboard
      // For now, components should guard against null activeCareProfile themselves.
    }
  }, [currentUser, activeCareProfile, navigate]);

  // Fetch Vitals History Effect
  useEffect(() => {
    if (!currentUser || !activeCareProfile) {
      // Guard with activeCareProfile
      setVitalsHistory([]);
      setIsLoadingVitalsHistory(false);
      return;
    }
    setIsLoadingVitalsHistory(true);
    const vitalsCollectionRef = collection(db, "vitalsLog");
    const q = query(
      vitalsCollectionRef,
      where("careProfileId", "==", activeCareProfile.id),
      orderBy("createdAt", "desc"),
      limit(5),
    );
    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const history = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setVitalsHistory(history);
        setIsLoadingVitalsHistory(false);
      },
      (error) => {
        console.error("Error fetching vitals history for dashboard: ", error);
        setIsLoadingVitalsHistory(false);
      },
    );
    return () => unsubscribe();
  }, [currentUser, activeCareProfile]); // Add activeCareProfile to dependency array

  // Fetch Incidents Effect (Dashboard)
  useEffect(() => {
    if (!currentUser || !activeCareProfile) {
      // Guard with activeCareProfile
      setDashboardIncidents([]);
      return;
    }
    const incidentsCollectionRef = collection(db, "incidentReports");
    const fourteenDaysAgo = new Date();
    fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14);
    const q = query(
      incidentsCollectionRef,
      where("careProfileId", "==", activeCareProfile.id),
      where("dateTime", ">=", Timestamp.fromDate(fourteenDaysAgo)),
      orderBy("dateTime", "desc"),
      limit(5),
    );
    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const incidents = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setDashboardIncidents(incidents);
      },
      (error) => {
        console.error("Error fetching incidents for dashboard: ", error);
      },
    );
    return () => unsubscribe();
  }, [currentUser, activeCareProfile]); // Add activeCareProfile to dependency array

  // Fetch Appointments Effect (Dashboard)
  useEffect(() => {
    if (!currentUser || !activeCareProfile) {
      setDashboardAppointments([]);
      setIsLoadingDashboardAppointments(false);
      return;
    }
    setIsLoadingDashboardAppointments(true);
    const appointmentsCollectionRef = collection(db, "appointments");
    const now = Timestamp.now();
    const q = query(
      appointmentsCollectionRef,
      where("careProfileId", "==", activeCareProfile.id),
      where("dateTime", ">=", now),
      orderBy("dateTime", "asc"),
      limit(5),
    );
    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const appointments = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setDashboardAppointments(appointments);
        setIsLoadingDashboardAppointments(false);
      },
      (error) => {
        console.error("Error fetching appointments for dashboard: ", error);
        setIsLoadingDashboardAppointments(false);
      },
    );
    return () => unsubscribe();
  }, [currentUser, activeCareProfile]);

  // Fetch Notes & Observations Effect (Dashboard)
  useEffect(() => {
    if (!currentUser || !activeCareProfile) {
      setDashboardNotes([]);
      return;
    }
    const notesCollectionRef = collection(db, "notesObservations");
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const q = query(
      notesCollectionRef,
      where("careProfileId", "==", activeCareProfile.id),
      where("createdAt", ">=", Timestamp.fromDate(thirtyDaysAgo)),
      orderBy("createdAt", "desc"),
      limit(5),
    );
    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const notes = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setDashboardNotes(notes);
      },
      (error) => {
        console.error("Error fetching notes for dashboard: ", error);
      },
    );
    return () => unsubscribe();
  }, [currentUser, activeCareProfile]);

  // MEDICATION ADMINISTRATION LOG - Fetch Data
  useEffect(() => {
    let unsubOptions = () => {};
    let unsubHistory = () => {};
    if (!currentUser || !activeCareProfile) {
      setMedsMedicationOptions([]);
      setMedsAdministrationHistory([]);
      setIsLoadingMeds(false);
      return;
    }
    setIsLoadingMeds(true);
    const medsRef = collection(db, "medications");
    const qMeds = query(
      medsRef,
      where("status", "in", ["Active", "PRN"]),
      where("careProfileId", "==", activeCareProfile.id),
      orderBy("medicationName", "asc"),
    );
    unsubOptions = onSnapshot(
      qMeds,
      (snapshot) => {
        const opts = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setMedsMedicationOptions(opts);
        setIsLoadingMeds(false);
      },
      (err) => {
        console.error("Error fetching med options for dashboard: ", err);
        setIsLoadingMeds(false);
      },
    );
    const histRef = collection(db, "medicationAdministrationLog");
    const qHist = query(
      histRef,
      where("careProfileId", "==", activeCareProfile.id),
      orderBy("administeredAt", "desc"),
      limit(10),
    );
    unsubHistory = onSnapshot(
      qHist,
      (snapshot) => {
        const hist = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setMedsAdministrationHistory(hist);
      },
      (err) => {
        console.error("Error fetching med admin history for dashboard: ", err);
      },
    );

    return () => {
      unsubOptions();
      unsubHistory();
    };
  }, [currentUser, activeCareProfile]);

  const todayCareSummary = React.useMemo(
    () => buildTodaySummary(todayEvents, todayTaskLogs),
    [todayEvents, todayTaskLogs],
  );

  const scheduledMedicationReminders = React.useMemo(
    () =>
      getScheduledMedicationReminders(
        medsMedicationOptions,
        medsAdministrationHistory,
      ),
    [medsMedicationOptions, medsAdministrationHistory],
  );

  const todayAttentionItems = React.useMemo(() => {
    const items = [];
    const now = new Date();
    const fourHoursFromNow = new Date(now.getTime() + 4 * 60 * 60 * 1000);
    const nextMedication = scheduledMedicationReminders.find(
      (medication) => medication.nextDose <= fourHoursFromNow,
    );
    const todayAppointment = dashboardAppointments.find((appointment) => {
      const appointmentDate = toDateSafe(appointment.dateTime);
      return (
        appointmentDate && getLocalDateString(appointmentDate) === todayDateStr
      );
    });
    const openTaskCount = Math.max(
      todayCareSummary.taskTotal - todayCareSummary.taskCompleted,
      0,
    );

    if (lowStockInventory.length > 0) {
      const details = lowStockInventory
        .slice(0, 3)
        .map((item) => {
          const status = getInventoryStockStatus(item);
          return `${item.medicationName || "Medication"} (${formatInventoryQuantity(
            item,
          )}${status === "Out of stock" ? ", out" : ""})`;
        })
        .join("; ");
      const remainingCount = lowStockInventory.length - 3;
      items.push({
        id: "low-stock-inventory",
        tone: lowStockInventory.some(
          (item) => getInventoryStockStatus(item) === "Out of stock",
        )
          ? "red"
          : "amber",
        title: `${lowStockInventory.length} medication${
          lowStockInventory.length === 1 ? "" : "s"
        } low on stock`,
        detail: `${details}${remainingCount > 0 ? `; +${remainingCount} more` : ""}.`,
        actionLabel: "Open Inventory →",
        to: "/medications/ordering",
      });
    }

    if (todayCareSummary.incidentCount > 0) {
      items.push({
        id: "incidents-today",
        tone: "red",
        title: "Incident recorded today",
        detail: `${todayCareSummary.incidentCount} incident${
          todayCareSummary.incidentCount === 1 ? "" : "s"
        } need review.`,
        actionLabel: "Open Daily Care Log →",
        to: DAILY_CARE_LOG_PATH,
      });
    }

    if (nextMedication) {
      items.push({
        id: "medication-due-soon",
        tone: "amber",
        title: "Medication due soon",
        detail: `${nextMedication.medicationName || "Medication"}${
          nextMedication.strength ? ` (${nextMedication.strength})` : ""
        } at ${formatTimeLabel(nextMedication.nextDose)}.`,
        actionLabel: "Open Daily Care Log →",
        to: DAILY_CARE_LOG_PATH,
      });
    }

    if (openTaskCount > 0) {
      items.push({
        id: "tasks-open",
        tone: "amber",
        title: "Tasks still open",
        detail: `${openTaskCount} checklist item${
          openTaskCount === 1 ? "" : "s"
        } remaining. ${todayCareSummary.nextIncompleteTask || ""}`.trim(),
        actionLabel: "Open Tasks →",
        to: "/notes-incidents/tasks",
      });
    }

    if (todayCareSummary.vitalsCount === 0) {
      items.push({
        id: "missing-vitals",
        tone: "blue",
        title: "No vitals recorded today",
        detail: "Add today's vitals when the next check is complete.",
        actionLabel: "Add Daily Care Log →",
        to: DAILY_CARE_LOG_PATH,
      });
    }

    if (todayCareSummary.dailyCareCount === 0) {
      items.push({
        id: "missing-daily-care",
        tone: "blue",
        title: "No daily care log yet",
        detail:
          "Capture meals, hygiene, meds, notes, and incidents in one log.",
        actionLabel: "Add Daily Care Log →",
        to: DAILY_CARE_LOG_PATH,
      });
    }

    if (todayAppointment) {
      items.push({
        id: "appointment-today",
        tone: "blue",
        title: "Appointment today",
        detail: `${todayAppointment.reason || "Appointment"}${
          todayAppointment.doctorName
            ? ` with ${todayAppointment.doctorName}`
            : ""
        } at ${formatTimeLabel(todayAppointment.dateTime)}.`,
        actionLabel: "Open Appointments →",
        to: "/appointments",
      });
    }

    return items;
  }, [
    dashboardAppointments,
    lowStockInventory,
    scheduledMedicationReminders,
    todayCareSummary,
    todayDateStr,
  ]);

  const isLoadingTodayOverview =
    isLoadingTodayEvents ||
    isLoadingTodayTasks ||
    isLoadingMeds ||
    isLoadingLowStockInventory;

  if (!activeCareProfile) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center bg-gray-50">
        <h2 className="text-2xl font-semibold text-gray-700 mb-4">
          No Active Care Profile Selected
        </h2>
        <p className="text-gray-500 mb-6">
          Please select or create a care profile to view the dashboard.
        </p>
        <button
          onClick={() => navigate("/profiles")}
          className="px-6 py-2 bg-purple-600 text-white font-semibold rounded-lg shadow-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-600 focus:ring-opacity-50"
        >
          Go to Profiles
        </button>
      </div>
    );
  }

  const renderGlance = () => (
    <div className="mx-auto max-w-7xl space-y-5 rounded-[2rem] bg-slate-50/70 p-2 md:space-y-6 md:p-4">
      <div className="space-y-5 md:space-y-6">
        <TodaysAttention
          items={todayAttentionItems}
          isLoading={isLoadingTodayOverview}
        />
        <TodaysCareSnapshot
          summary={todayCareSummary}
          isLoading={isLoadingTodayOverview}
          onViewDailyActivity={() => setActiveTab("summary")}
        />
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 2xl:grid-cols-3">
        <RecentVitals
          vitals={vitalsHistory[0]}
          isLoading={isLoadingVitalsHistory}
        />
        <GlanceSection title="Upcoming Medications" eyebrow="Medication">
          <MedicationReminders
            medicationOptions={medsMedicationOptions}
            administrationHistory={medsAdministrationHistory}
            isLoading={isLoadingMeds}
          />
        </GlanceSection>
        <GlanceSection title="Upcoming Appointments" eyebrow="Schedule">
          <UpcomingAppointments
            appointments={dashboardAppointments}
            isLoading={isLoadingDashboardAppointments}
          />
        </GlanceSection>
        <GlanceSection title="Recent Incidents" eyebrow="Safety">
          <RecentIncidents incidents={dashboardIncidents} />
        </GlanceSection>
        <GlanceSection title="Recent Notes" eyebrow="Observations">
          <RecentNotes notes={dashboardNotes} />
        </GlanceSection>
      </div>
    </div>
  );

  const renderDailySummary = () => (
    <div className="space-y-4">
      <div className="bg-white p-4 rounded shadow">
        <h2 className="text-lg font-semibold mb-2">Daily Activity</h2>
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <input
            type="date"
            value={summaryDate}
            onChange={(e) => updateSummaryDate(e.target.value)}
            className="border rounded px-3 py-2"
          />
          <button
            onClick={() => {
              // Initialize the range to the current selected summary date when opening
              setSummaryStartDate(summaryDate);
              setSummaryEndDate(summaryDate);
              setIsRangeExportOpen(true);
            }}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded"
          >
            Export PDF
          </button>
        </div>
        {isRangeExportOpen && (
          <div className="mt-4 flex flex-col md:flex-row md:items-center gap-4 p-4 border rounded bg-gray-50">
            <input
              type="date"
              value={summaryStartDate}
              onChange={(e) => setSummaryStartDate(e.target.value)}
              className="border rounded px-3 py-2"
            />
            <span className="text-gray-500">to</span>
            <input
              type="date"
              value={summaryEndDate}
              onChange={(e) => setSummaryEndDate(e.target.value)}
              className="border rounded px-3 py-2"
            />
            <button
              onClick={exportRangePdf}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded"
            >
              Generate Report
            </button>
            <button
              onClick={() => setIsRangeExportOpen(false)}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded"
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      {dailyEvents.length > 0 && (
        <div className="bg-white p-4 rounded shadow">
          {/* Mobile cards */}
          <div className="space-y-3 md:hidden">
            {dailyEvents.map((ev, idx) => (
              <div
                key={idx}
                className="bg-gray-50 rounded p-3 shadow-sm flex flex-col gap-1"
              >
                <div className="text-xs text-gray-500">
                  {ev.ts.toLocaleTimeString()}
                </div>
                <div className="text-sm text-gray-700 break-words whitespace-pre-wrap">
                  {formatEventDetails(ev)}
                </div>
              </div>
            ))}
          </div>

          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-2 text-left">Time</th>
                  <th className="px-4 py-2 text-left">Details</th>
                </tr>
              </thead>
              <tbody>
                {dailyEvents.map((ev, idx) => (
                  <tr key={idx} className="border-b">
                    <td className="px-4 py-2 whitespace-nowrap">
                      {ev.ts.toLocaleTimeString()}
                    </td>
                    <td
                      className="px-4 py-2 break-words whitespace-pre-wrap"
                      colSpan="2"
                    >
                      {formatEventDetails(ev)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="p-2">
      {/* Tab buttons */}
      <div className="flex gap-2 mb-4">
        <button
          className={`px-4 py-2 rounded-t font-semibold ${
            activeTab === "glance"
              ? "bg-purple-600 text-white"
              : "bg-gray-200 text-gray-700"
          }`}
          onClick={() => setActiveTab("glance")}
        >
          At a Glance
        </button>
        <button
          className={`px-4 py-2 rounded-t font-semibold ${
            activeTab === "summary"
              ? "bg-purple-600 text-white"
              : "bg-gray-200 text-gray-700"
          }`}
          onClick={() => setActiveTab("summary")}
        >
          Daily Activity
        </button>
      </div>

      {/* Tab content */}
      {activeTab === "glance" ? renderGlance() : renderDailySummary()}
    </div>
  );
};

export default DashboardPage;
